/**
 * Deep linking into the Hopn passenger app.
 *
 * The app registers the custom scheme `hopn://`. `hopn://ride/:id` opens the app on
 * that trip's detail screen (mapped by the app's React Navigation `linking` config).
 * On the web we attempt that link and, if the app isn't installed, fall back to the
 * platform store.
 */

const APP_SCHEME = 'hopn';
// Real Android applicationId (from android/app/build.gradle), NOT app.json's stale android.package.
const ANDROID_PACKAGE = 'com.hopenpassenger';
// TODO(store): replace 0000000000 with the real App Store numeric id once published.
const IOS_APP_ID = '0000000000';

// Web (https) store pages — used by the desktop download-app popup badges.
export const STORE_URLS = {
    android: `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`,
    ios: `https://apps.apple.com/app/id${IOS_APP_ID}`,
};

// Native store schemes — open the Play Store / App Store APP directly (so an
// unpublished-app "not found" shows inside the store app, not a browser tab).
// Used by the mobile deep-link fallback.
const NATIVE_STORE_URLS = {
    android: `market://details?id=${ANDROID_PACKAGE}`,
    ios: `itms-apps://apps.apple.com/app/id${IOS_APP_ID}`,
};

/** Deep link to a trip's detail screen in the app. Keep the path in sync with the app's linking config. */
export function tripDeepLink(tripId) {
    return `${APP_SCHEME}://ride/${tripId}`;
}

function isAndroid() {
    if (typeof navigator === 'undefined') return false;
    return /android/i.test(navigator.userAgent || '');
}

function isIOS() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // iPadOS 13+ reports as desktop Safari, so treat a touch-capable Mac as iOS.
    const iOSDevice = /iP(ad|hone|od)/.test(ua);
    const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return iOSDevice || iPadOS;
}

/** 'android' | 'ios' | 'desktop' — branches the "open the app" flow. */
export function getDevice() {
    if (isAndroid()) return 'android';
    if (isIOS()) return 'ios';
    return 'desktop';
}

/**
 * Mobile "open the app, else store" flow. Navigates to the `hopn://` link; if the
 * app opens the page goes to the background (visibilitychange), which cancels the
 * store redirect. If nothing handled the scheme, after a short delay we send the
 * user to the correct store.
 *
 * @param {string|number} tripId
 * @param {'android'|'ios'} [device] resolved device (defaults to getDevice()).
 */
export function openTripInAppOrStore(tripId, device) {
    const dev = device || getDevice();
    const storeUrl = dev === 'android' ? NATIVE_STORE_URLS.android : NATIVE_STORE_URLS.ios;

    let done = false;
    const goStore = () => {
        if (done || document.hidden) return;
        done = true;
        cleanup();
        window.location.href = storeUrl;
    };
    const onVisibility = () => {
        if (document.hidden) {
            done = true; // app opened — don't bounce to the store
            cleanup();
        }
    };
    const timer = setTimeout(goStore, 1500);
    const cleanup = () => {
        clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pagehide', onVisibility);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onVisibility);

    // Kick off the app open.
    window.location.href = tripDeepLink(tripId);
}
